import ControllerBase from '../../../../global/js/ControllerBase';
import { MoleculeFormProps } from './m-form.data';
import { MoleculeFormEvents, MoleculeFormEventTypes } from './m-form.events';
import { OrganismFormStepperEvents } from "../../organisms/o-form-stepper/o-form-stepper.events";

interface ComponentRefs {
}

type HTMLInputTypes = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

export default class ControllerControlledForm extends ControllerBase<ComponentRefs> {

	private static readonly componentRefs: ComponentRefs = {};

	private inputs: Record<string, HTMLInputTypes> = {};
	/** Initialize form validity state */
	private formValidity = true;
	/** A list of translations for the component */
	private readonly translations: MoleculeFormProps['translations'];
	private readonly lastControlKeyUp: boolean;
	private currentElementWithKeyupListener: HTMLInputTypes | undefined;
	private currentTimer: NodeJS.Timeout;

	constructor(element: HTMLElement) {
		super(element, [], Object.values(ControllerControlledForm.componentRefs));
		try {
			this.translations = this.$element.dataset.translations && JSON.parse(this.$element.dataset.translations);
			this.lastControlKeyUp = this.$element.dataset.lastcontrolinvalidevent === 'keyup' ? true : false;
		} catch (error) {
			console.warn(error, 'Missing translations for m-form');
		}

		this.init()
			.validateFields()
			.listenForChanges()
			.observeValidationUpdateRequests()
			.observeFormValidationErrors();
	}

	private init(): ControllerControlledForm {
		// collect a list of inputs, selects, textareas
		this.getFields()
			.forEach((input: HTMLInputTypes) => {
				if (input && input.name) {
					// check for non-unique names!
					if (Object.prototype.hasOwnProperty.call(this.inputs, input.name)) {
						console.warn('Form input name is not unique: ', input.name);
					}
					this.inputs[input.name] = input;
				}
			});

		return this;
	}

	private getFields(): Array<HTMLInputTypes> {
		return Array
			.from<HTMLInputTypes>(this.$element.querySelectorAll('input, textarea, select'));
	}

	/**
	 * Runs form validation and optionally emits the validity event
	 * @url https://developer.mozilla.org/en-US/docs/Learn/Forms/Form_validation
	 * @param  {HTMLInputTypes|null} currentFormElement
	 * @param  {boolean=true} emitOnlyOnChanged
	 * @returns ControllerControlledForm
	 */
	private validateFields(currentFormElement: HTMLInputTypes | null = null, emitOnlyOnChanged: boolean = true): ControllerControlledForm {
		this.dispatchCustomEvent(OrganismFormStepperEvents.FORM_DATA_CHANGED_BY_STEP, 0, false, true)

		// remove old error message from the form element if it exists
		currentFormElement && this.clearValidationErrors(currentFormElement);
		// if field is required and value empty, add a required error message
		currentFormElement && this.showHTML5ValidationErrors(currentFormElement);

		const validity = this.runValidation();
		this.formValidity = validity.currentValidState;

		// dispatch the form's validity state to the $element. Listening parent components can listen using a reference
		if (emitOnlyOnChanged && validity.validStateChanged) {
			this.dispatchCustomEvent(MoleculeFormEvents.VALIDATION_RAN, this.formValidity, false, false);
		} else {
			this.dispatchCustomEvent(MoleculeFormEvents.VALIDATION_RAN, this.formValidity, false, false);
		}
		return this;
	}

	/**
	 * Perform HTML5 validation on collected form fields and report changes to the validation state
	 * @returns CustomValidity
	 */
	private runValidation(): CustomValidity {
		let valid: boolean = true;
		const currentValidity = this.formValidity;
		const invalidInputs: HTMLInputTypes[] = [];

		for (const key in this.inputs) {
			if (Object.prototype.hasOwnProperty.call(this.inputs, key)) {
				const element = this.inputs[key];
				if (element.validity.valid === false) {

					if (this.lastControlKeyUp) {
						// remember that the input was invalid
						invalidInputs.push(element);
					}

					// test inputs to find at least one that is not valid
					if (valid === true) {
						valid = false;
					}
				}
			}
		}

		// set up keyup events for the last invalid input
		if (invalidInputs.length === 1) {
			const elInv = invalidInputs[0];
			if (elInv.tagName.toUpperCase() === 'INPUT') {
				elInv.addEventListener('keyup', (e) => this.onKeyup(e, elInv));
				this.currentElementWithKeyupListener = elInv;
			}
		} else if (this.currentElementWithKeyupListener) {
			// reset keyup event listeners
			this.currentElementWithKeyupListener.removeEventListener('keyup', (e) => this.onKeyup(e, this.currentElementWithKeyupListener as HTMLInputTypes));
			clearTimeout(this.currentTimer);
			this.currentElementWithKeyupListener = undefined;
		}

		const validity: CustomValidity = {
			previousValidState: currentValidity,
			currentValidState: valid,
			validStateChanged: currentValidity !== valid,
		};
		return validity;
	}

	private onKeyup(e: Event, elInv: HTMLInputTypes) {
		if (this.currentTimer) {
			clearTimeout(this.currentTimer);
		}
		this.currentTimer = setTimeout(() => {
			this.validateFields(elInv);
		}, 500);
	}

	private listenForChanges(): ControllerControlledForm {
		for (const key in this.inputs) {
			if (Object.prototype.hasOwnProperty.call(this.inputs, key)) {
				const elem = this.inputs[key];
				switch (elem.tagName.toUpperCase()) {
					case 'INPUT': {
						elem.addEventListener('blur', (e) => {
							this.validateFields(elem);
						});
						break;
					}
					case 'RADIO': // todo
					case 'TEXTAREA':
					case 'SELECT': {
						elem.addEventListener('change', (e) => this.validateFields(elem))
						break;
					}
				}
			}
		}
		return this;
	}

	/**
	 * Support for re-running validation from parent component
	 * @returns ControllerControlledForm
	 */
	private observeValidationUpdateRequests(): ControllerControlledForm {
		this.$element.addEventListener(MoleculeFormEvents.RERUN_VALIDATION, (event: CustomEvent<MoleculeFormEventTypes['RERUN_VALIDATION']>) => {
			if (event.detail) {
				this.validateFields(null, false);
			}
		});
		return this;
	}

	private observeFormValidationErrors(): ControllerControlledForm {
		window.addEventListener(MoleculeFormEvents.DISPLAY_VALIDATION_ERRORS, (event: CustomEvent<MoleculeFormEventTypes['DISPLAY_VALIDATION_ERRORS']>) => {
			if (event.detail) {
				this.updateValidationErrors(event.detail);
			}
		});
		return this;
	}

	private updateValidationErrors(errors: MoleculeFormEventTypes['DISPLAY_VALIDATION_ERRORS']): void {
		if (!errors) return;
		this.inputUtils.addErrorsToFieldset({ element: this.$element, errors })
	}

	private clearValidationErrors(formElement: HTMLInputTypes): ControllerControlledForm {
		this.inputUtils.addOrRemoveFormValidationError('HIDE_VALIDATION_ERROR', formElement);
		return this;
	}

	/**
	 * Show additional client-side validation errors, such as when a field is 'touched' by the user, show a required validation error if the field in required and invalid
	 * @param  {HTMLInputTypes} formElement
	 * @returns ControllerControlledForm
	 */
	private showHTML5ValidationErrors(formElement: HTMLInputTypes): ControllerControlledForm {
		if (formElement.required && formElement.validity.valid === false) {
			this.inputUtils.addOrRemoveFormValidationError('SHOW_VALIDATION_ERROR', formElement, (this.translations.fieldIsRequired || 'This field is required.'));
		}
		// ...more support for patterns and general invalid fields, min length etc...?
		return this;
	}
}

interface CustomValidity {
	/** The previous HTML5 validation state of the form elements */
	previousValidState: boolean;
	/** The current or new HTML5 validation state of the form elements after checking validation */
	currentValidState: boolean;
	/** True if the state changed since last check */
	validStateChanged: boolean;
}
